import { WizardStep } from '@factorialco/deprecated-design-system'
import { compact } from 'lodash'

import i18n from 'modules/core/lib/i18n'

import { BasicInfoStep } from '../BasicInfoStep'
import { CommentsStep } from '../CommentsStep'
import { EmployeeSelectorStep } from '../EmployeeSelectorStep'
import { TimingAndCostStep } from '../TimingAndCostStep'
import { TrainingNeedStep } from '../TrainingNeedStep'
import { TrainingSelectorStep } from '../TrainingSelectorStep'
import { WizardData } from '../types'

interface WizardConfig {
  fromCatalog?: boolean
  trainingId?: number
  allowDraft?: boolean
}

interface UserProps {
  excludedEmployeeIds?: number[]
  managedEmployeeIds?: number[]
  manageEveryone?: boolean
}

interface AmplitudeEvent {
  submit: string
  cancel: string
  back: string
  next: string
  start: string
}

const createSecondaryAction = (
  allowDraft: boolean,
  handleDraftSubmit: (data: WizardData) => void
) => {
  return allowDraft
    ? {
        action: (data: WizardData) => handleDraftSubmit(data),
        label: i18n.t('trainings.training_requests.create.save_as_draft'),
      }
    : undefined
}

export const useWizardSteps = (
  wizardConfig: WizardConfig,
  user: UserProps,
  handleDraftSubmit: (data: WizardData) => void,
  amplitudeEvent: AmplitudeEvent
): WizardStep<WizardData>[] => {
  const { fromCatalog = false, trainingId, allowDraft = true } = wizardConfig
  const { managedEmployeeIds: managedEmployees, manageEveryone, excludedEmployeeIds } = user

  const secondaryAction = createSecondaryAction(allowDraft, handleDraftSubmit)

  const employeeSelectorStep: WizardStep<WizardData> = {
    title: i18n.t('trainings.training_requests.create.employee_selector_step'),
    nextText: i18n.t('common.next'),
    render: (props) => (
      <EmployeeSelectorStep
        amplitudeEventNext={amplitudeEvent.next}
        excludedIds={excludedEmployeeIds ?? []}
        manageEveryone={manageEveryone || false}
        {...props}
      />
    ),
    isCompleted: () => true,
    secondaryAction,
  }

  const trainingSelectorStep: WizardStep<WizardData> = {
    title: i18n.t('trainings.training_requests.create.training'),
    nextText: i18n.t('common.next'),
    render: (props) => <TrainingSelectorStep amplitudeEventNext={amplitudeEvent.next} {...props} />,
    isCompleted: (data) => !!data.trainingId,
    secondaryAction,
  }

  const createTrainingRequestWizardSteps: WizardStep<WizardData>[] = [
    {
      title: i18n.t('trainings.training_requests.create.training_need_step'),
      nextText: i18n.t('common.next'),
      render: (props) => <TrainingNeedStep amplitudeEventNext={amplitudeEvent.next} {...props} />,
      isCompleted: (data) => !!data.trainingNeed,
      secondaryAction,
    },
    {
      title: i18n.t('trainings.training_requests.create.basic_info_step'),
      nextText: i18n.t('common.next'),
      render: (props) => <BasicInfoStep amplitudeEventNext={amplitudeEvent.next} {...props} />,
      isCompleted: (data) => !!data.trainingName,
      secondaryAction,
    },
    {
      title: i18n.t('trainings.training_requests.create.timing_cost_step.title'),
      nextText: i18n.t('common.next'),
      render: (props) => <TimingAndCostStep amplitudeEventNext={amplitudeEvent.next} {...props} />,
      isCompleted: () => true,
      secondaryAction,
    },
    {
      title: i18n.t('trainings.training_requests.create.comments_step'),
      nextText: i18n.t('trainings.training_requests.create.send_for_approval'),
      render: (props) => <CommentsStep amplitudeEventNext={amplitudeEvent.next} {...props} />,
      isCompleted: () => true,
      secondaryAction,
    },
  ]

  const createFromCatalogSteps: WizardStep<WizardData>[] = [
    {
      title: i18n.t('trainings.training_requests.create.training_need_step'),
      nextText: i18n.t('common.next'),
      render: (props) => <TrainingNeedStep amplitudeEventNext={amplitudeEvent.next} {...props} />,
      isCompleted: (data) => !!data.trainingNeed,
      secondaryAction,
    },
    {
      title: i18n.t('trainings.training_requests.create.comments_step'),
      nextText: i18n.t('trainings.training_requests.create.send_for_approval'),
      render: (props) => <CommentsStep amplitudeEventNext={amplitudeEvent.next} {...props} />,
      isCompleted: () => true,
      secondaryAction,
    },
  ]

  if (fromCatalog) {
    const trainingStep = trainingId ? null : trainingSelectorStep
    const employeeStep =
      managedEmployees && managedEmployees.length > 0 ? employeeSelectorStep : null
    return compact([
      ...createFromCatalogSteps.slice(0, 1),
      trainingStep,
      employeeStep,
      ...createFromCatalogSteps.slice(1),
    ])
  }

  if (managedEmployees && managedEmployees.length > 0) {
    return [
      ...createTrainingRequestWizardSteps.slice(0, 3),
      employeeSelectorStep,
      ...createTrainingRequestWizardSteps.slice(3),
    ]
  }
  return createTrainingRequestWizardSteps
}
